import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts"
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@components/common/chart"

const activityChartConfig = {
  tasks: { label: "Tasks" },
  created: { label: "Created", color: "#3b82f6" },
  finished: { label: "Finished", color: "#10b981" },
} satisfies ChartConfig

export function TaskActivityChart({
  data,
  isHebrew,
  translate,
  onPointClick,
}: {
  data: { date: string; created: number; finished: number }[]
  isHebrew: boolean
  translate: (key: string) => string
  onPointClick?: (date: string, metric: "created" | "finished") => void
}) {
  return (
    <ChartContainer config={activityChartConfig} className='aspect-auto h-[260px] w-full'>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id='fillCreated' x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0%' stopColor='#3b82f6' stopOpacity={0.35} />
            <stop offset='100%' stopColor='#3b82f6' stopOpacity={0.02} />
          </linearGradient>
          <linearGradient id='fillFinished' x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0%' stopColor='#10b981' stopOpacity={0.35} />
            <stop offset='100%' stopColor='#10b981' stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} strokeDasharray='3 3' stroke='#f0f0f0' />
        <XAxis
          dataKey='date'
          tickLine={false}
          axisLine={false}
          tickMargin={10}
          minTickGap={40}
          reversed={isHebrew}
          tick={{ fontSize: 11, fill: "#6b7280" }}
          tickFormatter={(value) => {
            const date = new Date(value)
            const loc = isHebrew ? "he" : "en-US"
            return date.toLocaleDateString(loc, { month: "short", day: "numeric" })
          }}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={10}
          tick={{ fontSize: 11, fill: "#6b7280" }}
          orientation={isHebrew ? "right" : "left"}
          allowDecimals={false}
        />
        <ChartTooltip
          cursor={{ stroke: "#e5e7eb", strokeWidth: 1 }}
          content={
            <ChartTooltipContent
              labelFormatter={(value) => {
                const loc = isHebrew ? "he" : "en-US"
                return new Date(value).toLocaleDateString(loc, {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })
              }}
              indicator='dot'
            />
          }
        />
        <Area
          dataKey='created'
          name={translate("created")}
          type='monotoneX'
          fill='url(#fillCreated)'
          stroke='#3b82f6'
          strokeWidth={2.5}
          dot={false}
          activeDot={{
            r: 4,
            fill: "#3b82f6",
            style: onPointClick ? { cursor: "pointer" } : undefined,
            onClick: onPointClick
              ? (_e: any, payload: any) => {
                  const date = payload?.payload?.date
                  if (date) onPointClick(date, "created")
                }
              : undefined,
          }}
        />
        <Area
          dataKey='finished'
          name={translate("finished")}
          type='monotoneX'
          fill='url(#fillFinished)'
          stroke='#10b981'
          strokeWidth={2.5}
          dot={false}
          activeDot={{
            r: 4,
            fill: "#10b981",
            style: onPointClick ? { cursor: "pointer" } : undefined,
            onClick: onPointClick
              ? (_e: any, payload: any) => {
                  const date = payload?.payload?.date
                  if (date) onPointClick(date, "finished")
                }
              : undefined,
          }}
        />
        <ChartLegend content={<ChartLegendContent />} />
      </AreaChart>
    </ChartContainer>
  )
}
